/**
 * Preferred passkey transport (the "mobile-first" preference).
 *
 * Persists the user's authenticator choice — `this_device`
 * (platform: Touch ID / Face ID / Windows Hello),
 * `other_device` (hybrid: phone with QR), or `security_key`
 * (roaming USB / NFC / BLE) — so returning visitors default
 * to their prior choice.
 *
 * This is the single source of truth for every enrollment /
 * re-enrollment surface: admin invite enrollment, portal
 * JIT enrollment, recovery re-enrollment, "Add a passkey"
 * dialog. Consolidating here prevents drift — every surface
 * uses the same storage key and the same default.
 *
 * The mobile-first default is `other_device` — PureID is a
 * passkey-forward product and QR-on-phone is the dominant
 * enterprise passkey enrollment flow. Operators can override
 * per-tenant via EnrollmentTransports policy; this local
 * storage is just the "remember my last click" affordance.
 */

/** A real WebAuthn authenticator transport — the kind of thing a
 * `navigator.credentials.create()/.get()` ceremony actually runs
 * against on THIS device:
 *
 *   this_device   — platform authenticator (Touch ID / Face ID /
 *                   Windows Hello), authenticatorAttachment=platform
 *   other_device  — hybrid / caBLE (phone over Bluetooth + QR)
 *   security_key  — roaming USB / NFC / BLE hardware key
 *
 * This type is deliberately PURE: every value is something a
 * ceremony, a stored preference, and a post-ceremony
 * classification can all legitimately produce. Anything that
 * isn't a real transport (e.g. PureID's app-layer QR enrollment
 * channel) MUST NOT live here — see EnrollmentChoice. Keeping
 * this union honest is what stops the "treat a flow-selector
 * like a transport" class of bug. */
export type PasskeyTransport =
  | "this_device"
  | "other_device"
  | "security_key";

/** What the enrollment picker can emit. A superset of
 * PasskeyTransport that adds the two NON-transport options:
 *
 *   pureid_qr — PureID's app-layer QR enrollment channel. NOT a
 *              WebAuthn transport: picking it does NOT run a
 *              ceremony on this device; it hands off to the
 *              phone (which runs create() against the tenant RP
 *              ID). Enrollment-only — never a sign-in card, never
 *              a stored preference, never a ceremony-classified
 *              outcome.
 *
 *   any       — "no preference": run the ceremony HERE, but send
 *              no hint, so the browser's own picker offers
 *              everything the tenant's authenticatorSelection
 *              allows. Also not a transport — it is the ABSENCE
 *              of a transport request, so it cannot be stored as
 *              a preference (there is no card to re-select) and
 *              cannot be a ceremony outcome (the authenticator
 *              always reports something concrete; read it with
 *              deriveActualTransport). It is the same value the
 *              backend already recognises: `"any"` is a member of
 *              ceremony.KnownUIPreferences and maps to nil hints,
 *              byte-identical to sending no hint at all — pinned
 *              by TestNoPreferenceCardsAreIdentical.
 *
 * The boundary rule (load-bearing): a consumer that receives an
 * EnrollmentChoice MUST narrow it exactly once — route `pureid_qr`
 * to the QR flow, keep `any` as a wire hint only, and pass the
 * remaining PasskeyTransport to the local ceremony. Use
 * `isPasskeyTransport` for that narrowing rather than comparing
 * against the non-transport members by hand: the members are what
 * change, and the predicate is the one place that has to notice.
 * Downstream of that single narrowing, code deals only in
 * PasskeyTransport, so neither non-transport value can leak into
 * ceremony / storage / classification paths. This is the type
 * that makes the "Card 2 is different" trap structurally
 * impossible to re-introduce silently. */
export type EnrollmentChoice = PasskeyTransport | "pureid_qr" | "any";

/**
 * Normalise a tenant's enrollment-card allowlist as it arrives on
 * the wire (`tenants.enrollment_transports`).
 *
 * EMPTY OR ABSENT BECOMES `undefined`, which every picker renders as
 * "no policy supplied" — all cards. That mapping is the load-bearing
 * part: a real tenant's allowlist can never be empty, because the
 * column's CHECK requires at least one member, so
 * an empty array can only mean the projection did not carry one. It
 * happens for real — an infrastructure-tenant enrollment flow strips
 * the tenant projection to render platform chrome, and an older
 * backend has no field at all.
 *
 * Reading empty as "nothing is allowed" would filter every card away
 * and put a first-time user on the no-working-method screen, unable
 * to enroll, because of a field nobody set. Availability fails OPEN;
 * only the card that WIDENS the OS prompt fails closed (see
 * offersNoPreferenceCard).
 */
export function normaliseEnrollmentCards(
  cards: readonly string[] | null | undefined,
): readonly string[] | undefined {
  return cards && cards.length > 0 ? cards : undefined;
}

/**
 * Whether the tenant's allowlist offers the no-preference card
 * ("Show all options", wire value `any`).
 *
 * Absent, empty, or simply not listed → false. This one fails
 * CLOSED, unlike the rest of card availability, because the card
 * sends no WebAuthn hint: it widens what the browser's prompt
 * offers rather than restoring a standard path, and "we could not
 * confirm the tenant allows it" is not a yes.
 */
export function offersNoPreferenceCard(
  cards: readonly string[] | null | undefined,
): boolean {
  return cards?.includes("any") ?? false;
}

/**
 * Whether the picker's controlled `value` can hold this card.
 *
 * `pureid_qr` is an ACTION card: clicking it routes to the QR
 * enrollment surface, and `onChange("pureid_qr")` is a deliberate
 * no-op at every consumer because the selection is
 * PasskeyTransport-shaped where it is stored. So a selection set to
 * it is a selection nobody reads — the card renders unselected, the
 * Continue button acts on whatever was picked before, and nothing
 * says why. `any` IS selectable: it is not a transport, but it is a
 * pick the ceremony carries through as its wire hint.
 *
 * Exhaustive for the same reason as isPasskeyTransport: a new
 * EnrollmentChoice member has to be classified here on purpose.
 */
export function isSelectableCard(c: EnrollmentChoice): boolean {
  switch (c) {
    case "this_device":
    case "other_device":
    case "security_key":
    case "any":
      return true;
    case "pureid_qr":
      return false;
  }
}

/**
 * The narrowing the boundary rule above requires, as one named
 * predicate: true when the picker emitted a REAL transport, false
 * for the non-transport picks (`pureid_qr`, `any`).
 *
 * Deliberately a switch and not `c !== "pureid_qr" && c !== "any"`:
 * the switch is exhaustive over EnrollmentChoice, so adding a
 * member to that union fails the build HERE — at the one place
 * whose job is to know the difference — instead of silently
 * classifying the new member as a transport at a dozen call sites
 * that each wrote the comparison out by hand. Those hand-written
 * comparisons are what this replaces; they were correct only
 * because `pureid_qr` happened to be the only non-transport.
 */
export function isPasskeyTransport(c: EnrollmentChoice): c is PasskeyTransport {
  switch (c) {
    case "this_device":
    case "other_device":
    case "security_key":
      return true;
    case "pureid_qr":
    case "any":
      return false;
  }
}

/** THIS-DEVICE FIRST. Used when no prior preference is persisted and no recommendation is wired.
 *
 * This reverses an earlier mobile-first default, deliberately and for one reason: the earlier rule
 * made the first passkey a Google Password Manager credential on a Windows desktop. On Chrome the
 * WebAuthn L3 hint alone is advisory, so offering anything other than the platform authenticator
 * lets the browser route to its own password manager, and the enrolling user ends up with a
 * credential in a consumer vault instead of in the OS keystore their employer controls.
 *
 * Every other transport is still one click away in the picker, and a user who picks one has it
 * remembered for next time — the point is the DEFAULT, not the available set. Applies identically
 * to platform admins and tenant users; a default that differs by surface reads as random to the
 * person using it, which is how this was found. */
export const PASSKEY_TRANSPORT_DEFAULT: PasskeyTransport = "this_device";

/**
 * Capability-aware product default. The single rule the picker
 * lands on when nothing else (localStorage, server-side cross-
 * device pref, caller-supplied recommendation) has voted yet.
 *
 *   1. `hybridFeasibility !== false` → `"other_device"` (QR /
 *      phone — the mobile-first product mantra). `null` is
 *      permissive: Safari / Firefox can't probe BLE, so they
 *      default to mobile-first which is the right broad bet.
 *      Only an explicit `false` from the probe (BLE absent /
 *      RDP-AVD sniff / two learned failures) demotes off
 *      mobile-first.
 *   2. else `platformAuthenticator !== false` → `"this_device"`
 *      (the device has a UV-capable platform authenticator and
 *      hybrid isn't going to work).
 *   3. else → `"security_key"` (last viable category — the
 *      picker still capability-disables it if the user has no
 *      such credential, but it's the only suggestion left).
 *
 * Pure helper — no React, no DOM. Takes the same `Pick`-ed
 * shape the picker's per-card disable logic uses, so a partial
 * snapshot (or `null` for "probe still in flight") is the
 * legitimate input on first paint. The `useCeremonyTransport`
 * hook re-runs this resolver via the FU-5 late-arrival
 * useEffect when the snapshot resolves, so the picker re-seeds
 * once we know the truth — gated on `hasPickedRef` so a user
 * who has clicked already keeps their pick.
 */
export function defaultTransportForCapability(
  capability:
    | {
        hybridFeasibility: boolean | null;
        platformAuthenticator: boolean | null;
      }
    | null
    | undefined,
): PasskeyTransport {
  // No snapshot yet → the constant above. FU-5 re-runs the cascade when the snapshot arrives.
  if (!capability) return PASSKEY_TRANSPORT_DEFAULT;
  // THE PLATFORM AUTHENTICATOR WINS WHEN IT EXISTS. The previous order tested hybrid first, and
  // hybrid is feasible on essentially every desktop, so "this device" was unreachable as a default
  // even on a machine with Windows Hello sitting right there. Offer the OS keystore first and fall
  // back through the cross-device path to a roaming key.
  if (capability.platformAuthenticator !== false) return "this_device";
  if (capability.hybridFeasibility !== false) return "other_device";
  return "security_key";
}

const PASSKEY_TRANSPORT_STORAGE_KEY = "pureid.enrollment.preferredTransport";

/**
 * Load the user's prior transport choice, or return the
 * mobile-first default. Safe in SSR / privacy-mode browsers
 * — returns the default when localStorage is unavailable
 * or access throws.
 *
 * NOTE: this function ALWAYS returns a value (defaulting to
 * `PASSKEY_TRANSPORT_DEFAULT`). Callers that need to layer
 * additional priority sources (e.g., a server-configured
 * tenant default) should use `loadPreferredPasskeyTransportOrNull`
 * below — that variant returns null when no preference is stored,
 * letting the caller fall through to the next layer cleanly.
 */
export function loadPreferredPasskeyTransport(): PasskeyTransport {
  return loadPreferredPasskeyTransportOrNull() ?? PASSKEY_TRANSPORT_DEFAULT;
}

/**
 * Same as loadPreferredPasskeyTransport but returns null when
 * no valid preference is stored. Use this when the caller wants
 * to layer in a tenant-config default ahead of the hardcoded
 * mobile-first fallback:
 *
 *   const hint =
 *     loadPreferredPasskeyTransportOrNull()  // user pick
 *     ?? loginConfig?.default_hint            // tenant policy
 *     ?? "other_device";                      // hardcoded
 *
 * Without this distinction (the older single-function pattern),
 * tenant-policy defaults are silently unreachable because
 * loadPreferredPasskeyTransport always returns truthy.
 */
export function loadPreferredPasskeyTransportOrNull(): PasskeyTransport | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(PASSKEY_TRANSPORT_STORAGE_KEY);
    if (
      raw === "this_device" ||
      raw === "other_device" ||
      raw === "security_key"
    ) {
      return raw;
    }
  } catch {
    // ignore — privacy-mode browsers throw on access
  }
  return null;
}

/**
 * Persist the user's choice so subsequent sessions default
 * to it. No-op when localStorage is unavailable.
 */
export function savePreferredPasskeyTransport(t: PasskeyTransport): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(PASSKEY_TRANSPORT_STORAGE_KEY, t);
  } catch {
    // ignore
  }
}

/**
 * isTransportAvailable reports whether the supplied
 * PasskeyTransport choice is reachable given the deduped
 * transport-tag list from /identify (KnownUserResult.
 * available_transports). Empty / undefined input is
 * permissive ("we don't know — let the picker enable
 * everything"), preserving the legacy behaviour for
 * credentials that predate transport tracking.
 *
 * Mapping (mirrors the picker UI):
 *   "other_device"  reachable when "hybrid" is in the list
 *   "this_device"   reachable when "internal" is in the list
 *   "security_key"  reachable when any of "usb"/"nfc"/"ble"
 */
export function isTransportAvailable(
  preference: PasskeyTransport,
  available: readonly string[] | undefined,
): boolean {
  if (!available || available.length === 0) return true;
  // Exhaustive over PasskeyTransport. `pureid_qr` is absent by
  // type — it's an EnrollmentChoice, never a sign-in credential's
  // transport, so this availability check can't be asked about it.
  switch (preference) {
    case "other_device":
      return available.includes("hybrid");
    case "this_device":
      return available.includes("internal");
    case "security_key":
      return (
        available.includes("usb") ||
        available.includes("nfc") ||
        available.includes("ble")
      );
  }
}

// resolveDefaultTransport (formerly defined here) was the
// pre-hook composer that picked an initial transport for the
// picker. It's been superseded by `useCeremonyTransport`
// (packages/ui/src/hooks/use-ceremony-transport.ts) which
// layers the same localStorage check with the server-side
// recommendation AND (post-H-1) the user's cross-device
// preference, all behind a single React-friendly hook.
//
// Removed 2026-05-14 as part of the H-series cleanup: the
// only production callers had already migrated to the hook;
// the function survived as a JSDoc reference in
// transport-picker.tsx and its own test file. Both updated
// in the same commit.
